<template lang="pug">
el-drawer.mark-received-drawer(
    :title="$t('Mark return as received')"
    :visible.sync="visibilityInternal"
    direction="rtl"
    size="40%"
    :wrapper-closable="false"
    @closed="reset"
)
    section.body(v-if="cancellation")
        p.hint {{ $t('Review each item the customer claimed and record what physically arrived. Items not received stay open on the cancellation.') }}
        table.received-table
            thead
                tr
                    th.col-item {{ $t('Item') }}
                    th.col-qty {{ $t('Received') }}
                    th.col-restock {{ $t('Restock') }}
                    th.col-stock {{ $t('Target stock') }}
            tbody
                tr(v-for="row in rows" :key="`row-${row.purchased_item_id}`")
                    td.col-item
                        .item-name {{ row.name || row.sku }}
                        small.item-claimed {{ $t('Claimed') }}: {{ row.claimed_qty }}
                    td.col-qty
                        el-input-number(
                            v-model="row.qty"
                            size="mini"
                            :min="0"
                            :max="row.claimed_qty"
                            :controls-position="'right'"
                            :step-strictly="true"
                        )
                    td.col-restock
                        el-switch(
                            v-model="row.restock"
                            :disabled="row.qty === 0"
                        )
                    td.col-stock
                        el-select(
                            v-model="row.stock_id"
                            size="mini"
                            :disabled="!row.restock || row.qty === 0"
                            :placeholder="$t('Select stock')"
                            filterable
                        )
                            el-option(
                                v-for="stock in stockOptions"
                                :key="stock.id"
                                :value="parseInt(stock.id)"
                                :label="stock.name || stock.key || `Stock #${stock.id}`"
                            )
    .drawer-footer
        el-button(
            size="mini"
            icon="el-icon-fa-times fas"
            @click="visibilityInternal = false"
        ) {{ $t('Cancel') }}
        el-button(
            size="mini"
            type="primary"
            icon="el-icon-fa-check fas"
            :loading="saving"
            :disabled="!canSubmit"
            @click="submit"
        ) {{ $t('Confirm receipt') }}
</template>

<i18n>
{
    "en": {
        "Mark return as received": "Mark return as received",
        "Review each item the customer claimed and record what physically arrived. Items not received stay open on the cancellation.": "Review each item the customer claimed and record what physically arrived. Items not received stay open on the cancellation.",
        "Item": "Item",
        "Received": "Received",
        "Claimed": "Claimed",
        "Restock": "Restock",
        "Target stock": "Target stock",
        "Select stock": "Select stock",
        "Cancel": "Cancel",
        "Confirm receipt": "Confirm receipt",
        "Receipt recorded": "Receipt recorded",
        "Receipt could not be recorded": "Receipt could not be recorded"
    },
    "de": {
        "Mark return as received": "Eingang der Rücksendung erfassen",
        "Review each item the customer claimed and record what physically arrived. Items not received stay open on the cancellation.": "Prüfen Sie pro Artikel, was tatsächlich eingegangen ist. Nicht eingegangene Artikel bleiben offen auf dem Widerruf.",
        "Item": "Artikel",
        "Received": "Eingegangen",
        "Claimed": "Beantragt",
        "Restock": "Einbuchen",
        "Target stock": "Ziel-Lager",
        "Select stock": "Lager auswählen",
        "Cancel": "Abbrechen",
        "Confirm receipt": "Eingang bestätigen",
        "Receipt recorded": "Wareneingang erfasst",
        "Receipt could not be recorded": "Wareneingang konnte nicht erfasst werden"
    }
}
</i18n>

<script>
import _ from 'lodash';
import PurchaseApi from '@/extensions/shop/store/api/purchase';

export default {
    name: 'MarkReceivedDrawer',
    props: {
        visibility: { type: Boolean, default: false },
        cancellation: { type: Object, default: null },
        stockOptions: { type: Array, default: () => [] },
    },
    data: () => ({
        rows: [],
        saving: false,
    }),
    computed: {
        visibilityInternal: {
            get() { return this.visibility; },
            set(value) { this.$emit('update:visibility', value); },
        },
        canSubmit() {
            // Allow submit even when all rows are qty=0 (admin acknowledges
            // "no items arrived" — the backend will still set received_at).
            return !!this.cancellation && !this.saving;
        },
    },
    watch: {
        cancellation: {
            handler() { this.buildRows(); },
            immediate: true,
        },
        // The drawer's @closed handler resets rows to []. When the admin
        // reopens the same cancellation, the `cancellation` reference is
        // unchanged so the watch above doesn't re-fire, and the table stays
        // empty. Rebuild explicitly whenever the drawer opens.
        visibility(value) {
            if (value && this.cancellation) this.buildRows();
        },
    },
    methods: {
        buildRows() {
            const items = _.get(this.cancellation, 'items') || [];
            this.rows = items.map(item => {
                const originalStockId = _.get(item, 'stocks.0.id');
                return {
                    purchased_item_id: parseInt(item.id),
                    name: item.name,
                    sku: item.sku,
                    claimed_qty: _.get(item, 'pivot.qty', 1),
                    qty: _.get(item, 'pivot.qty', 1),
                    restock: true,
                    stock_id: originalStockId
                        ? parseInt(originalStockId)
                        : (this.stockOptions.length ? parseInt(this.stockOptions[0].id) : null),
                };
            });
        },
        reset() {
            this.rows = [];
            this.saving = false;
        },
        async submit() {
            if (!this.cancellation) return;
            this.saving = true;
            const payload = this.rows.map(row => ({
                purchasedItem: { id: row.purchased_item_id },
                qty: parseInt(row.qty) || 0,
                restock: !!row.restock,
                stock: row.restock && row.stock_id ? { id: parseInt(row.stock_id) } : null,
            }));
            try {
                await PurchaseApi.markCancellationReceived(this.cancellation.id, payload);
                this.$notify.success({
                    title: this.$t('Receipt recorded'),
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 4000,
                });
                this.$emit('success');
                this.visibilityInternal = false;
            } catch (err) {
                console.error(err);
                this.$notify.error({
                    title: this.$t('Receipt could not be recorded'),
                    message: err.message || '',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 6000,
                });
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style lang="sass" scoped>
.mark-received-drawer
    ::v-deep .el-drawer__body
        display: flex
        flex-direction: column

.body
    flex: 1
    padding: 16px 20px
    overflow-y: auto

.hint
    color: var(--color-text-secondary, #606266)
    font-size: 13px
    margin-bottom: 16px

.received-table
    width: 100%
    border-collapse: collapse
    th, td
        padding: 10px 8px
        text-align: left
        border-bottom: 1px solid var(--border-color, #ebeef5)
        vertical-align: middle
    th
        font-size: 12px
        font-weight: 600
        color: var(--color-text-secondary, #606266)
    .col-qty
        width: 130px
    .col-restock
        width: 90px
        text-align: center
    .col-stock
        width: 200px
    .item-name
        font-weight: 500
    .item-claimed
        color: var(--color-text-muted, #909399)
        font-size: 11px

.drawer-footer
    padding: 12px 20px
    border-top: 1px solid var(--border-color, #ebeef5)
    display: flex
    justify-content: flex-end
    gap: 8px
</style>
